import type { BookingState, ServiceType } from '@features/booking/state/types';

const VALID_SERVICE_TYPES: ServiceType[] = ['breakfast', 'brunch', 'lunch', 'dinner'];

function isValidServiceType(value: string): value is ServiceType {
  return VALID_SERVICE_TYPES.includes(value as ServiceType);
}

/** Validate date string format (YYYY-MM-DD). */
function isValidDate(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) && !isNaN(Date.parse(value));
}

/** Validate center-hour format (HH:mm or HH:mm:ss). */
function isValidHour(value: string): boolean {
  return /^\d{1,2}:\d{2}(:\d{2})?$/.test(value);
}

export interface DeepLinkParams {
  date?: string;
  partySize?: string;
  serviceType?: string;
  centerHour?: string;
  wlResId?: string;
}

/**
 * Booking-state fields a deep link may pre-fill. A structural subset of
 * BookingState so it stays assignable to `updateBooking`'s argument while the
 * resolver itself never imports the provider runtime.
 */
export type DeepLinkUpdates = Pick<
  BookingState,
  // These fields are required (not optional): the resolver always emits them, so
  // a later resolution reliably clears state a prior deep link set (shallow
  // merge). `deepLinkDateLocked` is `true` only for the date-only form; the
  // reschedule context (`suggestedFromWaitingListAlternatives`/`rescheduleId`)
  // is set only on the waiting-list route — both default off here.
  | 'selectedDate'
  | 'timeSlots'
  | 'selectedTime'
  | 'columnType'
  | 'specialEvent'
  | 'deepLinkDateLocked'
  | 'suggestedFromWaitingListAlternatives'
  | 'rescheduleId'
> &
  Partial<
    Pick<
      BookingState,
      | 'diners'
      | 'service'
      | 'deepLinkCenterHour'
    >
  >;

export interface DeepLinkResolution {
  /** Target path to navigate to. '/' means "invalid link, send home". */
  path: string;
  /** Booking-state updates to apply, or null when redirecting home. */
  updates: DeepLinkUpdates | null;
}

/**
 * Pure resolver for `/search/...` deep links. Walks the param chain
 * left-to-right; each additional valid segment advances the landing target
 * one step deeper into the flow. Stops at the first missing/invalid segment.
 *
 *   date                           -> /select-diners
 *   date + partySize               -> /select-service
 *   date + partySize + serviceType -> /select-time
 *   + centerHour (valid HH:mm or HH:mm:ss)     -> /select-time, auto-selecting the slot
 *
 * An invalid/absent date is unrecoverable -> redirect home.
 * Replicates the legacy AngularJS `home.search` route's param extraction.
 */
export function resolveDeepLink(p: DeepLinkParams): DeepLinkResolution {
  if (!p.date || !isValidDate(p.date)) {
    return { path: '/', updates: null };
  }

  // Reset slot-derived state, the date lock, AND the waiting-list reschedule
  // context so a stale prior session can't leak through. These resets are
  // load-bearing because updateBooking shallow-merges over persisted state:
  //  - the lock is re-enabled below only for the date-only form, so a later
  //    full-form/WL resolution must clear a lock a prior date-only link set,
  //    or the existing routes lose their DATE step;
  //  - the reschedule context is re-set below only on the waiting-list route,
  //    so a plain /search link opened after a WL link must clear it — otherwise
  //    `isReschedule` stays true and the fresh booking skips party-size.
  const updates: DeepLinkUpdates = {
    selectedDate: p.date,
    timeSlots: null,
    selectedTime: null,
    columnType: null,
    specialEvent: null,
    deepLinkDateLocked: false,
    suggestedFromWaitingListAlternatives: false,
    rescheduleId: null,
  };
  let path = '/select-diners';

  const parsedParty = p.partySize ? parseInt(p.partySize, 10) : NaN;
  if (!isNaN(parsedParty) && parsedParty >= 1) {
    updates.diners = parsedParty;
    path = '/select-service';

    if (p.serviceType && isValidServiceType(p.serviceType)) {
      updates.service = p.serviceType;
      path = '/select-time';

      if (p.centerHour && isValidHour(p.centerHour)) {
        updates.deepLinkCenterHour = p.centerHour.split(':').slice(0, 2).join(':');
      }

      // Waiting-list reschedule context only applies to the full chain.
      const parsedWlResId = p.wlResId ? parseInt(p.wlResId, 10) : NaN;
      if (!isNaN(parsedWlResId)) {
        updates.suggestedFromWaitingListAlternatives = true;
        updates.rescheduleId = parsedWlResId;
      }
    }
  } else {
    // Date-only deep link: lock the date so the DATE step is skipped in the
    // forward flow (guest goes party-size → service → time for this date).
    updates.deepLinkDateLocked = true;
  }

  return { path, updates };
}
